import { useQuery } from '@apollo/client';
import { PaymentCardType } from '@shinetools/pricing-plan-library';

import { isCreditCardPendingOrder } from 'common/creditCards/isCreditCardPendingOrder';
import useCompanyContext from 'common/hooks/useCompanyContext';

import { canOrderNewPhysicalCard } from '../../../../utils';
import { PaymentCardRequestDocument } from './paymentCardRequest.gql';

const usePaymentCardRequest = () => {
  const { companyProfileId, companyUserId } = useCompanyContext();

  if (!companyProfileId) {
    throw new Error('companyProfileId is undefined in "usePaymentCardRequest"');
  }

  if (!companyUserId) {
    throw new Error('companyUserId is undefined in "usePaymentCardRequest"');
  }

  const { data, ...hook } = useQuery(PaymentCardRequestDocument, {
    fetchPolicy: 'cache-and-network',
    variables: { companyProfileId, companyUserId },
  });

  const creditCards = data?.viewer.company.creditCards ?? [];
  const firstPhysicalActivePaymentCard = creditCards.find(
    (card) =>
      card.companyUserId === companyUserId &&
      card.isPhysical &&
      !card.isDefinitivelyBlocked,
  );

  const firstPhysicalActivePaymentCardNumber =
    firstPhysicalActivePaymentCard &&
    !isCreditCardPendingOrder(firstPhysicalActivePaymentCard)
      ? firstPhysicalActivePaymentCard.maskedPan
      : '';

  const ongoingPhysicalCardRequests =
    data?.viewer.company.teamMembersRequests.edges.map(
      (request) => request.node,
    ) ?? [];

  return {
    ...hook,
    canRequestPhysicalCard:
      ongoingPhysicalCardRequests.length === 0 &&
      canOrderNewPhysicalCard({
        canOrderPremiumCard:
          data?.viewer.company.currentPlan.paymentCard.type ===
          PaymentCardType.BusinessWorldDebitCard,
        cards: creditCards,
      }),
    firstPhysicalActivePaymentCardNumber,
  };
};

export default usePaymentCardRequest;
